import type { ApexOptions } from "apexcharts";
import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import type {
	BCCADMINBaseModelsNetworkStatusLatestSample,
	BCCADMINBaseModelsNetworkStatusTargetView
} from "../API/Models";
import { formatRelativeTime } from "../Helper/dates";
import useNetworkStatusData from "../hooks/useNetworkStatusData";

const HEALTHY_THRESHOLD = 350;
const DEGRADED_THRESHOLD = 800;

const getStatusVisual = (latest?: BCCADMINBaseModelsNetworkStatusLatestSample | null) => {
	if (!latest || !latest.sampledAt) {
		return {
			text: "No data",
			color: "#94a3b8",
			badge: "border border-slate-200 bg-slate-50 text-slate-600"
		};
	}

	if (!latest.isSuccess) {
		return {
			text: "Unavailable",
			color: "#f43f5e",
			badge: "border border-rose-200 bg-rose-50 text-rose-700"
		};
	}

	if (latest.pingMs <= HEALTHY_THRESHOLD) {
		return {
			text: "Healthy",
			color: "#10b981",
			badge: "border border-emerald-200 bg-emerald-50 text-emerald-700"
		};
	}

	if (latest.pingMs <= DEGRADED_THRESHOLD) {
		return {
			text: "Degraded",
			color: "#f59e0b",
			badge: "border border-amber-200 bg-amber-50 text-amber-700"
		};
	}

	return {
		text: "Slow",
		color: "#f97316",
		badge: "border border-orange-200 bg-orange-50 text-orange-700"
	};
};

const buildChartOptions = (color: string, categories: string[]): ApexOptions => ({
	chart: {
		type: "bar",
		height: 220,
		toolbar: { show: false },
		zoom: { enabled: false },
		sparkline: { enabled: categories.length < 6 }
	},
	dataLabels: { enabled: false },
	grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
	plotOptions: {
		bar: {
			columnWidth: "55%",
			borderRadius: 6,
			borderRadiusApplication: "end",
			dataLabels: {
				position: "top"
			}
		}
	},
	stroke: {
		show: true,
		width: 2,
		colors: ["transparent"]
	},
	xaxis: {
		categories,
		labels: {
			show: categories.length <= 12,
			style: { colors: "#94a3b8" }
		},
		axisBorder: { color: "#e2e8f0" },
		axisTicks: { color: "#e2e8f0" }
	},
	yaxis: {
		labels: {
			formatter: (value: number) => `${Math.max(value, 0).toFixed(0)} ms`,
			style: { colors: "#94a3b8" }
		}
	},
	tooltip: {
		theme: "light",
		y: {
			formatter: (value: number) => `${Math.max(value, 0).toFixed(0)} ms`
		}
	},
	colors: [color]
});

const pickAveragePing = (point: { averagePingMs?: number | null; pingMs?: number | null }) => {
	const avg = point.averagePingMs;
	if (typeof avg === "number" && !Number.isNaN(avg) && avg > 0) {
		return avg;
	}

	return point.pingMs ?? 0;
};

const normalizePoints = (target: BCCADMINBaseModelsNetworkStatusTargetView) => {
	const points = target.points ?? [];
	const categories = points.map((point) => {
		if (!point.slotStart) return "";
		return new Date(point.slotStart).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
	});
	const series = points.map((point) => Math.max(0, Math.round(pickAveragePing(point))));
	return { categories, series };
};

const formatEndpointLabel = (url?: string | null) => {
	if (!url) return "-";
	try {
		const parsed = new URL(url);
		return parsed.host || url;
	} catch {
		return url;
	}
};

/** Network target status monitoring with latency charts and health indicators. */
const NetworkStatus: React.FC = () => {
	const { data, loading, error, isRefreshing, refresh } = useNetworkStatusData(24);

	const targets = useMemo(() => data?.targets ?? [], [data]);

	const incidentTargets = useMemo(
		() =>
			targets
				.filter((target) => target.latest && !target.latest.isSuccess)
				.map((target) => ({
					label: target.label ?? target.target ?? "Unknown target",
					message: target.latest?.message ?? "Endpoint unavailable"
				})),
		[targets]
	);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-[0.3em] text-slate-500">Monitoring</p>
						<h1 className="text-2xl font-semibold text-slate-900">Network status</h1>
						<p className="text-sm text-slate-500">
							Last updated {data?.generatedAt ? formatRelativeTime(data.generatedAt) : "-"} · Scans every 5 minutes
						</p>
					</div>
					<button
						type="button"
						onClick={refresh}
						disabled={isRefreshing || loading}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						{isRefreshing ? (
							<>
								<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
								Refreshing
							</>
						) : (
							"Refresh"
						)}
					</button>
				</div>

				{error && (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
						{error}
					</div>
				)}

				{incidentTargets.length > 0 && (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 shadow-sm">
						<p className="font-semibold">Network degradations detected</p>
						<ul className="mt-1 space-y-1 text-xs text-rose-700">
							{incidentTargets.map((incident) => (
								<li key={incident.label}>
									<span className="font-semibold">{incident.label}:</span> {incident.message}
								</li>
							))}
						</ul>
					</div>
				)}

				{loading ? (
					<div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
						{Array.from({ length: 4 }).map((_, index) => (
							<div key={index} className="h-72 animate-pulse rounded-2xl border border-slate-100 bg-slate-50" />
						))}
					</div>
				) : targets.length === 0 ? (
					<div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center text-slate-500">
						<p className="text-lg font-semibold text-slate-900">No measurements yet</p>
						<p className="mt-2 text-sm text-slate-500">The scanner will populate this view after the first interval completes.</p>
					</div>
				) : (
					<div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
						{targets.map((target) => {
							const status = getStatusVisual(target.latest);
							const { categories, series } = normalizePoints(target);
							const chartOptions = buildChartOptions(status.color, categories);
							const latestPing =
								target.latest && target.latest.isSuccess
									? `${Math.round(target.latest.pingMs ?? 0)} ms`
									: target.latest
										? "Unavailable"
										: "-";
							const lastChecked = target.latest?.sampledAt ? formatRelativeTime(target.latest.sampledAt) : "-";
							const lastSlotAverage = (() => {
								const lastPoint = target.points && target.points.length > 0 ? target.points[target.points.length - 1] : null;
								if (!lastPoint) return "-";
								const value = pickAveragePing(lastPoint);
								return value > 0 ? `${Math.round(value)} ms` : "-";
							})();

							return (
								<article key={target.target ?? target.label} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
									<div className="flex items-start justify-between gap-3">
										<div>
											<p className="text-xs uppercase tracking-[0.3em] text-slate-400">{target.category}</p>
											<h2 className="text-xl font-semibold text-slate-900">{target.label}</h2>
											<p className="text-sm text-slate-500">{target.description}</p>
											{target.url && (
												<a
													className="mt-1 inline-flex items-center text-xs font-semibold text-slate-600 transition hover:text-slate-900"
													href={target.url}
													target="_blank"
													rel="noreferrer"
												>
													{formatEndpointLabel(target.url)} →
												</a>
											)}
										</div>
										<span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${status.badge}`}>
											{status.text}
										</span>
									</div>

									<div className="mt-4 flex-1">
										{series.length > 0 ? (
											<Chart
												options={chartOptions}
												series={[{ name: "Latency (ms)", data: series }]}
												type="bar"
												height={200}
											/>
										) : (
											<div className="flex h-56 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
												No samples in this window.
											</div>
										)}
									</div>

									<div className="mt-4 grid gap-4 border-t border-slate-100 pt-4 text-sm text-slate-600 sm:grid-cols-3 lg:grid-cols-4">
										<div>
											<p className="text-xs uppercase tracking-wide text-slate-500">Latest ping</p>
											<p className="text-lg font-semibold text-slate-900">{latestPing}</p>
											<p className="text-xs text-slate-500">Avg: {lastSlotAverage}</p>
										</div>
										<div>
											<p className="text-xs uppercase tracking-wide text-slate-500">Last check</p>
											<p className="text-sm text-slate-900">{lastChecked}</p>
										</div>
										<div>
											<p className="text-xs uppercase tracking-wide text-slate-500">Detail</p>
											<p className="text-sm text-slate-900">
												{target.latest?.message ? target.latest.message : "Responding normally"}
											</p>
										</div>
									</div>
								</article>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default NetworkStatus;

